import React from 'react';

const VERSION = '1.2.0';

const EMOJI = {
  idle: '😊',
  wake_listening: '😌',
  listening: '👂',
  transcribing: '🤔',
  thinking: '💭',
  speaking: '🗣️',
  error: '😔',
  quota: '😴',
};

const COLORS = {
  idle: '#f97316',
  wake_listening: '#a8a29e',
  listening: '#ef4444',
  transcribing: '#8b5cf6',
  thinking: '#3b82f6',
  speaking: '#10b981',
  error: '#6b7280',
  quota: '#6b7280',
};

const PULSE_STATUSES = ['listening', 'speaking', 'wake_listening'];

// ── Smiley (original) ──────────────────────────────────────────────────────

function SmileyFace({ status }) {
  const isPulsing = PULSE_STATUSES.includes(status);
  const color = COLORS[status] || COLORS.idle;
  const emoji = EMOJI[status] || EMOJI.idle;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      <div style={{
        width: 'min(200px, 45vw)',
        height: 'min(200px, 45vw)',
        borderRadius: '50%',
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '80px',
        boxShadow: `0 0 0 ${isPulsing ? '20px' : '0px'} ${color}40`,
        transition: 'all 0.3s ease',
        animation: isPulsing ? 'cf-pulse 1.5s infinite' : 'none',
      }}>
        {emoji}
      </div>
      <style>{`
        @keyframes cf-pulse {
          0% { box-shadow: 0 0 0 0px ${color}60; }
          100% { box-shadow: 0 0 0 30px ${color}00; }
        }
      `}</style>
    </div>
  );
}

// ── Waveform ───────────────────────────────────────────────────────────────

function waveAnimationName(status) {
  if (status === 'speaking') return 'cf-wave-speaking';
  if (status === 'thinking' || status === 'transcribing') return 'cf-wave-thinking';
  if (status === 'listening' || status === 'wake_listening') return 'cf-wave-listening';
  return 'cf-wave-idle';
}

function WaveformFace({ status }) {
  const color = COLORS[status] || COLORS.idle;
  const anim = waveAnimationName(status);
  const barCount = 5;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
    }}>
      <div style={{
        width: 'min(200px, 45vw)',
        height: 'min(200px, 45vw)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}>
        {Array.from({ length: barCount }).map((_, i) => (
          <div key={i} style={{
            width: '20px',
            borderRadius: '10px',
            backgroundColor: color,
            height: '30px',
            animation: `${anim} ${0.8 + i * 0.15}s ease-in-out infinite alternate`,
            animationDelay: `${i * 0.12}s`,
            transition: 'background-color 0.3s ease',
          }} />
        ))}
      </div>
      <style>{`
        @keyframes cf-wave-idle {
          0% { height: 24px; }
          100% { height: 36px; }
        }
        @keyframes cf-wave-listening {
          0% { height: 40px; }
          50% { height: 70px; }
          100% { height: 50px; }
        }
        @keyframes cf-wave-thinking {
          0% { height: 40px; }
          100% { height: 80px; }
        }
        @keyframes cf-wave-speaking {
          0% { height: 20px; }
          25% { height: 120px; }
          50% { height: 40px; }
          75% { height: 100px; }
          100% { height: 30px; }
        }
      `}</style>
    </div>
  );
}

// ── Text-only ──────────────────────────────────────────────────────────────

function textForStatus(status, companionName) {
  const name = companionName || 'Lena';
  switch (status) {
    case 'listening':
    case 'wake_listening':
      return { title: `${name} is listening...`, subtitle: null };
    case 'transcribing':
    case 'thinking':
      return { title: `${name} is thinking...`, subtitle: null };
    case 'speaking':
      return { title: `${name} is speaking...`, subtitle: null };
    case 'error':
      return { title: name, subtitle: 'Something went wrong' };
    case 'quota':
      return { title: name, subtitle: 'Taking a rest' };
    default:
      return { title: name, subtitle: 'Ready to talk' };
  }
}

function TextOnlyFace({ status, companionName }) {
  const { title, subtitle } = textForStatus(status, companionName);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: 'min(200px, 45vw)',
      gap: '12px',
    }}>
      <div style={{
        fontSize: '48px',
        fontWeight: '600',
        color: '#92400e',
        letterSpacing: '-0.5px',
        transition: 'all 0.3s ease',
      }}>
        {title}
      </div>
      {subtitle && (
        <div style={{
          fontSize: '20px',
          color: '#b45309',
          opacity: 0.7,
        }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────

export default function CompanionFace({ status, style = 'smiley', companionName }) {
  switch (style) {
    case 'waveform':
      return <WaveformFace status={status} />;
    case 'text-only':
      return <TextOnlyFace status={status} companionName={companionName} />;
    default:
      return <SmileyFace status={status} />;
  }
}
